import Vue from 'vue';
import Datepicker from "vuejs-datepicker";
import AjaxUtils from "../../util/ajax_utils";
import draggable from "vuedraggable";

import {
  monthsAgoISO,
  todayISO,
} from '../../util/dateUtils';

import {ApckPortletActivityLogs} from "./portlets/activitylogs";
import {ApckPortletUnfixedCounter} from "./portlets/unfixedcounter";
import {ApckPortletUnfixedTable} from "./portlets/unfixedtable";
import {ApckPortletUnfixedVulns} from "./portlets/unfixedvulns";
import {ApckPortletImpact} from "./portlets/impact";
import {ApckPortletRunningScans} from "./portlets/runningscans";
import {ApckPortletRecentScans} from "./portlets/recentscans";
import {ApckPortletMyVulns} from "./portlets/myvulns";
import {ApckPortletVulnHosts} from "./portlets/vulnhosts";
import {ApckPortletTotalScans} from "./portlets/totalscans";
import {ApckPortletScheduledScans} from "./portlets/scheduledscans";
import {ApckPortletTrend} from "./portlets/trend";
import {ApckPortletTracker} from "./portlets/tracker";
import {ApckPortletOWASP} from "./portlets/owasp";

export const ApckSimplePortal = /*#__PURE__*/ Vue.extend({
    props: {
    },
    data: function() {
        return {};
    },
    template: `
        <apck-panel no-border class="drag-holder">
            <slot />
        </apck-panel>
    `
});


export const ApckPortal = {
    components: {
        'draggable': draggable
    },
    props: {
        title: {
            type: String,
            default: 'Dashboard'
        },
        headerTitle: {
            type: Boolean,
            default: false,
            required: false
        },
        dashboardMode: {
            type: Boolean, // enables the dashbaord class for styling inner panels
            default: true
        },
        url: {
            type: String,
            default: '/sidebar/portlets'
        },
        update: {
            type: String,
            default: '/sidebar/update'
        },
        portletBaseUrl: {
            type: String,
            default: '/portlet'
        },
        vertical: {
            type: Boolean,
            default: false
        },
        sidePortal: {
            type: Boolean,
            default: false
        },
        availablePortlets: {
            type: Array,
            default: () => []
        },
        extraPortletProps: {
            type: Object,
            default: () => {{}}
        }
    },
    data: function () {
        return {
            cmp: this,
            portlets: [],
            portletComponents: [],
        }
    },
    beforeMount: function () {
        var cmp = this;
        if (cmp.url) {
            AjaxUtils.ajax_get({
                url: cmp.url,
                success: function (data) {
                    for (const portlet of data) {
                        if (portlet.ctype && cmp.getPortletComponent(portlet.ctype)) {
                            cmp.portlets.push(portlet);
                        } else {
                            console.log("Could not find portlet with ctype", portlet.ctype)
                        }
                    }
                },
                opts: {flash_form_errors: false, flash_404_errors: false}
            });
        }
    },
    methods: {
        getPortletComponent: function (ctype) {
            const portletCfg = this.availablePortlets.find(item => item.value === ctype);
            if (portletCfg) {
                return portletCfg.component;
            }
        },
        buildPortletProps: function (portlet) {
            return {...portlet, ...this.extraPortletProps};
        },
        addNew: function () {
            this.$lightbox.open({
                component: ApckModalPortletSettings,
                bind: {
                    portal: this,
                    availablePortlets: this.availablePortlets,
                },
                size: "wide",
            });
        },
        getPortalConfig: function () {
            const config = [];
            for (const portlet of this.portletComponents) {
                config.push(portlet.getConfig());
            }
            return config;
        },
        updatePortalConfig: function (portletCmp, success, fail) {
            const cmp = this;
            this.portlets = this.getPortalConfig();
            AjaxUtils.ajax_post({
                url: cmp.update,
                data: JSON.stringify({"config": this.portlets}),
                success: success,
                fail: fail
            })
        },
        deletePortlet: function (index) {
            const cmp = this;
            this.portlets.splice(index, 1);
            this.portletComponents.splice(index, 1);
            Vue.nextTick(function () {
                cmp.updatePortalConfig();
            })
        },
        dragEnd: function (evt) {
            if (evt.oldDraggableIndex === evt.newDraggableIndex) {
                return;
            }
            // synchronise portletComponents
            const el = this.portletComponents[evt.oldDraggableIndex];
            this.portletComponents.splice(evt.oldDraggableIndex, 1);
            this.portletComponents.splice(evt.newDraggableIndex, 0, el);
            // save new portal order
            this.updatePortalConfig();
        },
    },
    template: `
        <apck-panel 
            :title="title"
            :header-title="headerTitle"
            force-menu
            no-border
            no-padding
            title-bar
            :dashboard-mode="dashboardMode"
            :class="['apck-portal', this.vertical ? 'force_vertical': '']"
        >
            <template v-slot:menu>
                <slot name="menuButtons"></slot>
                <apck-button
                    icon="fa-regular fa-plus-circle"
                    class="title-button btn-primary"
                    :handler="addNew"
                    title="Add Portlet"
                    value="Add Portlet"
                />
            </template>
        
            <draggable :list="portlets" @end="dragEnd">
                <component
                    v-for="(portlet, index) in portlets"
                    :is="getPortletComponent(portlet.ctype)"
                    v-bind="buildPortletProps(portlet)"
                    :portal="cmp"
                    :portletBaseUrl="portletBaseUrl"
                    :size="sidePortal ? 'full' : portlet.size"
                    :key="portlet.ctype"
                    :index="index"
                />
            </draggable>
        </apck-panel>
    `
}


export const ApckModalPortletSettings = {
    props: {
        portal: null,
        availablePortlets: null,
        _selectedPortlet: null,
    },
    data: function () {
        return {
            selectedPortlet: null,
            selectedPortletCtype: null,
            options: {},
            changed: new Set(),
        };
    },
    created() {
        if (this._selectedPortlet) {
            this.selectedPortletCtype = this._selectedPortlet.$props.ctype;
            this.selectedPortlet = this.availablePortlets.find(p => p.value === this.selectedPortletCtype).component;
            this.loadSettings(this._selectedPortlet.$data.options);
        }
    },
    methods: {
        loadSettings: function (options) {
            const opts = JSON.parse(JSON.stringify(options));

            const key_order = ['title', 'size'];
            // sort the keys, to make sure "title" and "size" come before all the rest
            const orderedOpts = Object.fromEntries(
                key_order
                    .filter(key => key in opts) // only include "title/size" if they are in `opts`
                    .map(key => [key, opts[key]])
                    .concat(Object.entries(opts).filter(([key]) => !key_order.includes(key))) // add remaining keys in their original order
            );
            // remove size option on sidebar portals
            if (this.portal.sidePortal) {
                delete orderedOpts['size'];
            }
            this.options = orderedOpts;
        },
        selectPortlet: function (value) {
            // reset changed options
            this.changed.clear();

            const portlet = this.availablePortlets.find(p => p.value === value);
            if (portlet && 'component' in portlet) {
                this.selectedPortletCtype = value;
                this.selectedPortlet = portlet.component;

                // selectedPortlet component is not yet init'd, so we must build the default options manually
                const options = JSON.parse(JSON.stringify(this.selectedPortlet.options.data().options));
                const _componentProps = this.selectedPortlet.options.props;
                for (const key of Object.keys(options)) {
                    if (!("value" in options[key])) {
                        const value = _componentProps[key]?.default;
                        options[key].value = value ? value : options[key].default;
                        // also add the option to the `changed` list
                        this.changed.add(key);
                    }
                }

                this.loadSettings(options);
            }
        },
        getOptionValue(path) {
            let obj = this.options;
            for (const key of path.split('.')) {
                obj = obj[key];
            }
            return obj.value;
        },
        setOptionValue(object, path, value) {
            const keys = path.split('.');
            let obj = object;
            for (let i = 0; i < keys.length - 1; i++) {
                obj = obj[keys[i]];
            }
            Vue.set(obj[keys[keys.length - 1]], "value", value);
        },
        updateOptions: function (key, value) {
            this.setOptionValue(this.options, key, value);
            this.changed.add(key);
        },
        portletConfig: function () {
            const config = {'ctype': this.selectedPortletCtype};
            Object.entries(this.options).map(([key, val]) => {
                config[key] = val.value || val.default;
            });
            return config
        },
        updatePortlet: function () {
            const cmp = this;

            // check if we have changed the portlet type, or just the portlet settings
            if (this.selectedPortletCtype !== this._selectedPortlet.$props.ctype) {
                // changed ctype, so we need to replace the portlet
                const config = this.portletConfig();
                config['ctype'] = this.selectedPortletCtype;
                Vue.set(this.portal.$data.portlets, this._selectedPortlet.$attrs.index, config);
            } else {
                // ctypes match, so just update portlet settings
                for (const field of this.changed) {
                    this.setOptionValue(this._selectedPortlet.$data.options, field, this.options[field].value);
                }
            }

            this.$emit("close"); // close lightbox
            Vue.nextTick(function () {
                cmp.portal.updatePortalConfig();
            });

        },
        saveNewPortlet: function () {
            const cmp = this;
            const config = this.portletConfig();
            cmp.portal.$data.portlets.push(config);

            this.$emit("close"); // close lightbox
            Vue.nextTick(function () {
                cmp.portal.updatePortalConfig();
            });
        },
        getPreviewSettings: function () {
            // get values or default from options
            const opts = Object.fromEntries(Object.entries(this.options).map(([k, v]) => [k, v.value || v.default]));
            opts['size'] = 'full'; // always set to full width for preview
            return opts;
        },
        makePreviewKey: function () {
            const settings = this.getPreviewSettings();
            // we don't want to update the key when the title or size change, since these won't require re-rendering
            delete settings.title;
            delete settings.size;
            return JSON.stringify(settings);
        }
    },
    template: `
        <div class="apck-flex-box portlet-lightbox">
            <div style="width: 40%;">
                <apck-panel no-padding :title="_selectedPortlet ? 'Edit Portlet' : 'Add Portlet'" style="margin-bottom: 5px">
                    <apck-field>
                        <apck-select
                            v-model="selectedPortletCtype"
                            @input="selectPortlet($event)"
                            label="Portlet Type"
                            :options="availablePortlets"
                        />
                    </apck-field>
                </apck-panel>
                
                <apck-panel v-if="selectedPortlet" title="Portlet Settings">
                
                    <apck-field v-for="(option,key,index) in options">
                        <apck-text
                            v-if="option.type === 'string'"
                            :value="getOptionValue(key)"
                            @input="updateOptions(key, $event)"
                            :label="option.label"
                            :key="key"
                        />
                        
                        <apck-text
                            v-else-if="option.type === 'number'"
                            :value="getOptionValue(key)"
                            @input="updateOptions(key, $event)"
                            :label="option.label"
                            inputmode="numeric"
                            :key="key"
                        />
                        
                        <apck-checkbox
                            v-else-if="option.type === 'boolean'"
                            :value="getOptionValue(key)"
                            @input="updateOptions(key, $event)"
                            :label="option.label"
                            :key="key"
                        />
        
                        <apck-select
                            v-else-if="option.type === 'select'"
                            :value="getOptionValue(key)"
                            @input="updateOptions(key, $event)"
                            :label="option.label"
                            :options="option['choices']"
                            :key="key"
                        />
                    </apck-field>
                    
                    <div class="apck-devide apck-vbox"></div>
                    <apck-button
                        v-if="changed.size > 0 && _selectedPortlet"
                        title="Update"
                        value="Update"
                        icon="fa-save"
                        class="btn-primary"
                        :handler="updatePortlet"
                    />
                    <apck-button
                        v-else-if="changed.size > 0"
                        title="Save"
                        value="Save"
                        icon="fa-save"
                        class="btn-positive"
                        :handler="saveNewPortlet"
                    />
                </apck-panel>
            </div>
            
            <div style="width: 60%; margin: -5px -5px -5px 5px;">
                <component
                    :is="selectedPortlet"
                    v-bind="getPreviewSettings()"
                    :key="makePreviewKey()"
                    :portal="portal"
                    hide-menu
                />
            </div>

        </div>
    `,
};


const DashboardDatePickerModal = {
    components: {
        'datepicker': Datepicker,
    },
    props: {
        start_date: null,
        end_date: null,
    },
    data: function () {
        return {
            _start_date: null,
            _end_date: null,
            selectedPortlet: null,
            selectedPortletCtype: null,
            options: {},
            changed: new Set(),
        };
    },
    created() {
        this.$data._start_date = this.start_date;
        this.$data._end_date = this.end_date;
    },
    methods: {
        formatDate(date) {
            var cmp = this;
            if (date === null) {
                return '';
            }
            try {
                var tzoffset = date.getTimezoneOffset() * 60000;
                return (new Date(date.getTime() - tzoffset)).toISOString().slice(0, 10);
            } catch (e) {
                cmp.$flash({
                    type: 'error',
                    message: "Please enter a valid date in the format DD/MM/YYYY"
                });
                return '';
            }
        },
        setStartDate(value) {
            const date = this.formatDate(value);
            if (date === '') {
                this.$data._start_date = null;
            } else {
                this.$data._start_date = date
            }
        },
        setEndDate(value) {
            const date = this.formatDate(value)
            if (date === '') {
                this.$data._end_date = null;
            } else {
                this.$data._end_date = date;
            }
        },
        applyChanges() {
            this.$emit('updateDates', {start: this.$data._start_date, end: this.$data._end_date});
            this.$emit('close');
        }
    },
    template: `
        <div class="dashboard-date-picker">
            <div class="form-field">
                <label>Start Date</label>
                <datepicker
                    @selected="event => {setStartDate(event)}"
                    :value="start_date"
                    format="dd/MM/yyyy"
                    placeholder="Start Date"
                    :disabled-dates="{'from': end_date ? new Date(end_date) : new Date()}"
                    clear-button
                    clear-button-icon="fa-regular fa-times"
                    calendar-button
                    calendar-button-icon="fa-regular fa-calendar"
                />
            </div>
            <div class="form-field">
                <label>End Date</label>
                <datepicker
                    @selected="event => {setEndDate(event)}"
                    :value="end_date"
                    format="dd/MM/yyyy"
                    placeholder="End Date"
                    :disabled-dates="{'to': new Date(start_date), 'from': new Date()}"
                    clear-button
                    clear-button-icon="fa-regular fa-times"
                    calendar-button
                    calendar-button-icon="fa-regular fa-calendar"
                />
            </div>
            <apck-button
                class="btn-primary"
                :handler="applyChanges"
                title="Apply"
                value="Apply"
            />
        </div>
    `,
};


export const ApckDashboardPortal = {
    components: {
        'apck-portal': ApckPortal,
    },
    props: {
        portletsUrl: {
            type: String,
            default: '/dashboard/portlets'
        },
        updateUrl: {
            type: String,
            default: '/dashboard/update'
        },
        portletBaseUrl: {
            type: String,
            default: '/portlet'
        },
    },
    data: function () {
        return {
            availportlets: [
                {"value": "logsportlet", "label": "Activity Logs", "component": ApckPortletActivityLogs},
                {"value": "impactportlet", "label": "Impact Chart", "component": ApckPortletImpact},
                {"value": "owaspportlet", "label": "OWASP Top 10", "component": ApckPortletOWASP},
                {"value": "runningscansportlet", "label": "Running Scans", "component": ApckPortletRunningScans},
                {"value": "recentscansportlet", "label": "Recent Scans", "component": ApckPortletRecentScans},
                {"value": "trendportlet", "label": "Vulnerability Trend", "component": ApckPortletTrend},
                {"value": "trackerportlet", "label": "Vulnerability Tracker", "component": ApckPortletTracker},
                {"value": "totalscansportlet", "label": "Total Scans", "component": ApckPortletTotalScans},
                {"value": "scheduledscansportlet", "label": "Scheduled Scans", "component": ApckPortletScheduledScans},
                {"value": "unfixedtableportlet", "label": "Unfixed Table", "component": ApckPortletUnfixedTable},
                {"value": "unfixedcounterportlet", "label": "Unfixed Counter", "component": ApckPortletUnfixedCounter},
                {"value": "unfixedportlet", "label": "Vulnerabilities Status", "component": ApckPortletUnfixedVulns},
                {"value": "myvulnsportlet", "label": "My Vulnerabilities", "component": ApckPortletMyVulns},
                {"value": "vulnhostsportlet", "label": "Vulnerable Hosts", "component": ApckPortletVulnHosts}
            ],
            datePickerOpen: false,
            start_date: monthsAgoISO(1),
            end_date: todayISO(),
            refreshKey: 0,
        };
    },
    methods: {
        openDatePicker: function () {
            const cmp = this;
            this.$lightbox.open({
                component: DashboardDatePickerModal,
                title: "Apply date",
                bind: {
                    start_date: cmp.start_date,
                    end_date: cmp.end_date,
                },
                on: {
                    updateDates: ({start, end}) => {
                        cmp.start_date = start;
                        cmp.end_date = end;
                        // trigger all portlets to refresh
                        cmp.refreshKey += 1;
                    }
                },
                size: "narrow",
            })
        },
    },
    template: `
        <apck-portal
            title="My Reports"
            class="dashboard-portal"
            :update="updateUrl"
            :url="portletsUrl"
            :availablePortlets="availportlets"
            :portletBaseUrl="portletBaseUrl"
            :extraPortletProps="{start_date, end_date}"
            :key="refreshKey"
        >
            <template v-slot:menuButtons>
                <div
                    :class="['title-button', datePickerOpen ? 'active' : '']"
                    @click="openDatePicker()"
                >
                    <i class="fa-regular fa-calendar"></i>
                    {{ start_date | formatDate }} - {{ end_date | formatDate }}
                </div>
            </template>
        </apck-portal>
    `
}

export const ApckSidePortal = {
    components: {
        'apck-portal': ApckPortal,
    },
    props: {
        title: {
            type: String,
            default: 'Quick View'
        },
        portletsUrl: {
            type: String,
            default: '/sidebar/portlets'
        },
        updateUrl: {
            type: String,
            default: '/sidebar/update'
        },
        portletBaseUrl: {
            type: String,
            default: '/portlet'
        },
    },
    data: function () {
        return {
            availportlets: [
                {"value": "logsportlet", "label": "Activity Logs", "component": ApckPortletActivityLogs},
                {"value": "impactportlet", "label": "Impact Chart", "component": ApckPortletImpact},
                {"value": "owaspportlet", "label": "OWASP Top 10", "component": ApckPortletOWASP},
                {"value": "runningscansportlet", "label": "Running Scans", "component": ApckPortletRunningScans},
                {"value": "recentscansportlet", "label": "Recent Scans", "component": ApckPortletRecentScans},
                {"value": "trendportlet", "label": "Vulnerability Trend", "component": ApckPortletTrend},
                {"value": "trackerportlet", "label": "Vulnerability Tracker", "component": ApckPortletTracker},
                {"value": "totalscansportlet", "label": "Total Scans", "component": ApckPortletTotalScans},
                {"value": "scheduledscansportlet", "label": "Scheduled Scans", "component": ApckPortletScheduledScans},
                {"value": "unfixedtableportlet", "label": "Unfixed Table", "component": ApckPortletUnfixedTable},
                {"value": "unfixedcounterportlet", "label": "Unfixed Counter", "component": ApckPortletUnfixedCounter},
                {"value": "unfixedportlet", "label": "Vulnerabilities Status", "component": ApckPortletUnfixedVulns},
                {"value": "myvulnsportlet", "label": "My Vulnerabilities", "component": ApckPortletMyVulns},
                {"value": "vulnhostsportlet", "label": "Vulnerable Hosts", "component": ApckPortletVulnHosts}
            ],
            vert: true
        };
    },
    template: `
        <apck-portal
            :title="title"
            class="sidebar-portal"
            :update="updateUrl"
            :url="portletsUrl"
            :portletBaseUrl="portletBaseUrl"
            :availablePortlets="availportlets"
            :vertical="vert"
            :sidePortal="true"
        />
    `
}
